import Z_INDEX from '@databases/layout/z-index';
import { TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH } from '@databases/utils/constants';

export const MAX_PINNED_LEADING_FIELD_COUNT = 12;
const PINNED_LEADING_COLLAPSED_BORDER_OVERLAP_PX = 1;

export type PinnedLeadingFieldLayoutItem = {
  stickyLeftPx: number;
  isPinned: boolean;
  isFreezeBoundary: boolean;
  headerZIndex: number;
  bodyZIndex: number;
};

export type ComputePinnedLeadingLayoutParams = {
  pinnedLeadingFieldCount: number;
  fieldCount: number;
  showDragHandlerColumn: boolean;
  dragHandlerWidthPx: number;
  columnWidths: ReadonlyArray<number | undefined>;
};

export type ComputeMaxPinnedLeadingFieldCountParams = {
  fieldCount: number;
  showDragHandlerColumn: boolean;
  dragHandlerWidthPx: number;
  columnWidths: ReadonlyArray<number | undefined>;
  viewportWidthPx: number;
  minScrollableWidthPx?: number;
};

export type GetPinnedLeadingFieldCountAfterFieldReorderParams = {
  fieldIds: ReadonlyArray<string>;
  selectedFieldIds: ReadonlyArray<string>;
  destinationIndex: number;
  pinnedLeadingFieldCount: number;
};

export type GetPinnedLeadingFieldCountAfterFieldsRemovedParams = {
  fieldIds: ReadonlyArray<string>;
  removedFieldIds: ReadonlyArray<string>;
  pinnedLeadingFieldCount: number;
};

export type GetPinnedLeadingFieldCountAfterFieldsInsertedParams = {
  fieldIds: ReadonlyArray<string>;
  insertionIndex: number;
  insertedFieldCount?: number;
  pinnedLeadingFieldCount: number;
};

export type GetPinnedLeadingFieldCountAfterFieldsShownParams = {
  allFieldIds: ReadonlyArray<string>;
  visibleFieldIds: ReadonlyArray<string>;
  shownFieldIds: ReadonlyArray<string>;
  pinnedLeadingFieldCount: number;
};

export function resolveColumnWidthsForPinnedLayout(
  columnWidths: ReadonlyArray<number | undefined>
): number[] {
  return columnWidths.map((_, index) =>
    getColumnWidthForPinnedLeadingLayout(columnWidths, index)
  );
}

function getColumnWidthForPinnedLeadingLayout(
  columnWidths: ReadonlyArray<number | undefined>,
  index: number
): number {
  const width = columnWidths[index];
  if (width != null) {
    return width;
  }
  if (index === 0) {
    return 0;
  }
  return TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH;
}

export function sumWidths(
  columnWidths: ReadonlyArray<number | undefined>,
  fromInclusive: number,
  toExclusive: number
): number {
  let sum = 0;
  for (let i = fromInclusive; i < toExclusive; i++) {
    sum += columnWidths[i] ?? 0;
  }
  return sum;
}

export function getPinnedLeadingRightEdgePx({
  pinnedLeadingFieldCount,
  showDragHandlerColumn,
  dragHandlerWidthPx,
  columnWidths,
}: {
  pinnedLeadingFieldCount: number;
  showDragHandlerColumn: boolean;
  dragHandlerWidthPx: number;
  columnWidths: ReadonlyArray<number | undefined>;
}): number {
  if (pinnedLeadingFieldCount <= 0) {
    return 0;
  }

  const resolvedWidths = resolveColumnWidthsForPinnedLayout(columnWidths);
  const dragPx = showDragHandlerColumn ? dragHandlerWidthPx : 0;
  let rightEdgePx = dragPx;
  for (let i = 0; i <= pinnedLeadingFieldCount; i++) {
    rightEdgePx += getColumnWidthForPinnedLeadingLayout(resolvedWidths, i);
  }
  return rightEdgePx;
}

export function computeMaxPinnedLeadingFieldCount({
  fieldCount,
  showDragHandlerColumn,
  dragHandlerWidthPx,
  columnWidths,
  viewportWidthPx,
  minScrollableWidthPx = TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
}: ComputeMaxPinnedLeadingFieldCountParams): number {
  const fixedMax = Math.max(0, Math.min(fieldCount, MAX_PINNED_LEADING_FIELD_COUNT));

  if (!Number.isFinite(viewportWidthPx) || viewportWidthPx <= 0) {
    return fixedMax;
  }

  const maxPinnedRightEdgePx = Math.max(0, viewportWidthPx - minScrollableWidthPx);
  let maxPinnedLeadingFieldCount = 0;
  for (let count = 1; count <= fixedMax; count++) {
    const rightEdgePx = getPinnedLeadingRightEdgePx({
      pinnedLeadingFieldCount: count,
      showDragHandlerColumn,
      dragHandlerWidthPx,
      columnWidths,
    });

    if (rightEdgePx > maxPinnedRightEdgePx) {
      break;
    }

    maxPinnedLeadingFieldCount = count;
  }

  return maxPinnedLeadingFieldCount;
}

export function getFieldDropDestinationIndex({
  targetIndex,
  closestEdge,
}: {
  targetIndex: number;
  closestEdge: string | null;
}): number {
  return targetIndex + (closestEdge === 'right' ? 1 : 0);
}

export function computePinnedLeadingLayout(
  params: ComputePinnedLeadingLayoutParams
): PinnedLeadingFieldLayoutItem[] {
  const { fieldCount, showDragHandlerColumn, dragHandlerWidthPx, columnWidths } = params;
  const resolvedWidths = resolveColumnWidthsForPinnedLayout(columnWidths);
  const pinnedLeadingFieldCount = Math.max(
    0,
    Math.min(params.pinnedLeadingFieldCount, fieldCount)
  );

  const dragPx = showDragHandlerColumn ? dragHandlerWidthPx : 0;

  const perField: PinnedLeadingFieldLayoutItem[] = [];
  let stickyLeftPx = dragPx;
  for (let fieldIndex = 0; fieldIndex < fieldCount; fieldIndex++) {
    stickyLeftPx += getColumnWidthForPinnedLeadingLayout(resolvedWidths, fieldIndex);
    const isPinned = fieldIndex < pinnedLeadingFieldCount;
    const isFreezeBoundary =
      isPinned &&
      pinnedLeadingFieldCount > 0 &&
      fieldIndex === pinnedLeadingFieldCount - 1;
    const pinnedZIndexOffset = isPinned ? 1 : 0;
    const collapsedBorderOverlapPx =
      isPinned && stickyLeftPx > 0
        ? fieldIndex * PINNED_LEADING_COLLAPSED_BORDER_OVERLAP_PX
        : 0;
    perField.push({
      stickyLeftPx: stickyLeftPx - collapsedBorderOverlapPx,
      isPinned,
      isFreezeBoundary,
      headerZIndex: Z_INDEX.TABLE_LAYOUT_HEADER_ROW + pinnedZIndexOffset,
      bodyZIndex: Z_INDEX.TABLE_ROW + pinnedZIndexOffset,
    });
  }
  return perField;
}

export function getPinnedLeadingFieldCountAfterFieldReorder({
  fieldIds,
  selectedFieldIds,
  destinationIndex,
  pinnedLeadingFieldCount,
}: GetPinnedLeadingFieldCountAfterFieldReorderParams): number {
  const clampedPinnedLeadingFieldCount = Math.max(
    0,
    Math.min(pinnedLeadingFieldCount, fieldIds.length, MAX_PINNED_LEADING_FIELD_COUNT)
  );

  if (clampedPinnedLeadingFieldCount === 0 || selectedFieldIds.length === 0) {
    return clampedPinnedLeadingFieldCount;
  }

  let nextPinnedLeadingFieldCount = clampedPinnedLeadingFieldCount;
  for (const fieldId of selectedFieldIds) {
    const sourceIndex = fieldIds.indexOf(fieldId);
    if (sourceIndex < 0) {
      continue;
    }

    const wasPinned = sourceIndex < clampedPinnedLeadingFieldCount;
    if (wasPinned && destinationIndex > clampedPinnedLeadingFieldCount) {
      nextPinnedLeadingFieldCount--;
    } else if (!wasPinned && destinationIndex <= clampedPinnedLeadingFieldCount) {
      nextPinnedLeadingFieldCount++;
    }
  }

  return Math.max(
    0,
    Math.min(nextPinnedLeadingFieldCount, fieldIds.length, MAX_PINNED_LEADING_FIELD_COUNT)
  );
}

export function getPinnedLeadingFieldCountAfterFieldsRemoved({
  fieldIds,
  removedFieldIds,
  pinnedLeadingFieldCount,
}: GetPinnedLeadingFieldCountAfterFieldsRemovedParams): number {
  const clampedPinnedLeadingFieldCount = Math.max(
    0,
    Math.min(pinnedLeadingFieldCount, fieldIds.length, MAX_PINNED_LEADING_FIELD_COUNT)
  );

  if (clampedPinnedLeadingFieldCount === 0 || removedFieldIds.length === 0) {
    return clampedPinnedLeadingFieldCount;
  }

  const removedFieldIdsSet = new Set(removedFieldIds);
  let removedPinnedFieldCount = 0;
  for (const fieldId of removedFieldIdsSet) {
    const fieldIndex = fieldIds.indexOf(fieldId);
    if (fieldIndex >= 0 && fieldIndex < clampedPinnedLeadingFieldCount) {
      removedPinnedFieldCount++;
    }
  }

  return Math.max(0, clampedPinnedLeadingFieldCount - removedPinnedFieldCount);
}

export function getPinnedLeadingFieldCountAfterFieldsInserted({
  fieldIds,
  insertionIndex,
  insertedFieldCount = 1,
  pinnedLeadingFieldCount,
}: GetPinnedLeadingFieldCountAfterFieldsInsertedParams): number {
  const clampedPinnedLeadingFieldCount = Math.max(
    0,
    Math.min(pinnedLeadingFieldCount, fieldIds.length, MAX_PINNED_LEADING_FIELD_COUNT)
  );
  const clampedInsertedFieldCount = Math.max(0, Math.trunc(insertedFieldCount));

  if (
    clampedPinnedLeadingFieldCount === 0 ||
    clampedInsertedFieldCount === 0 ||
    insertionIndex < 0 ||
    insertionIndex > clampedPinnedLeadingFieldCount
  ) {
    return clampedPinnedLeadingFieldCount;
  }

  return Math.min(
    clampedPinnedLeadingFieldCount + clampedInsertedFieldCount,
    MAX_PINNED_LEADING_FIELD_COUNT
  );
}

export function getPinnedLeadingFieldCountAfterFieldsShown({
  allFieldIds,
  visibleFieldIds,
  shownFieldIds,
  pinnedLeadingFieldCount,
}: GetPinnedLeadingFieldCountAfterFieldsShownParams): number {
  let nextVisibleFieldIds = [...visibleFieldIds];
  let nextPinnedLeadingFieldCount = pinnedLeadingFieldCount;
  const shownFieldIdsInFieldOrder = [...new Set(shownFieldIds)].sort(
    (fieldIdA, fieldIdB) => allFieldIds.indexOf(fieldIdA) - allFieldIds.indexOf(fieldIdB)
  );

  for (const shownFieldId of shownFieldIdsInFieldOrder) {
    if (nextVisibleFieldIds.includes(shownFieldId)) {
      continue;
    }

    const shownFieldIndex = allFieldIds.indexOf(shownFieldId);
    if (shownFieldIndex < 0) {
      continue;
    }

    const insertionIndex = nextVisibleFieldIds.filter((fieldId) => {
      const fieldIndex = allFieldIds.indexOf(fieldId);
      return fieldIndex >= 0 && fieldIndex < shownFieldIndex;
    }).length;

    nextPinnedLeadingFieldCount = getPinnedLeadingFieldCountAfterFieldsInserted({
      fieldIds: nextVisibleFieldIds,
      insertionIndex,
      pinnedLeadingFieldCount: nextPinnedLeadingFieldCount,
    });
    nextVisibleFieldIds = [
      ...nextVisibleFieldIds.slice(0, insertionIndex),
      shownFieldId,
      ...nextVisibleFieldIds.slice(insertionIndex),
    ];
  }

  return nextPinnedLeadingFieldCount;
}
